import React, { useState } from 'react';

const faqs = [
  {
    question: 'How do virtual consultations work?',
    answer: 'Our virtual consultations are conducted through a secure, HIPAA-compliant video platform. You\'ll meet with one of our licensed medical professionals from the comfort of your home. The consultation typically lasts 45-60 minutes and includes a comprehensive health assessment, discussion of your symptoms and goals, and development of a personalized treatment plan.'
  },
  {
    question: 'Are the treatments FDA approved?',
    answer: 'Yes, all medications and treatments we prescribe are FDA-approved. We only work with licensed compounding pharmacies and established pharmaceutical manufacturers. Our medical team follows strict protocols to ensure all treatments meet the highest safety and efficacy standards.'
  },
  {
    question: 'How long before I see results?',
    answer: 'Results vary depending on the treatment and individual factors. Most patients begin to notice improvements within 2-4 weeks of starting treatment. Hormone therapy typically shows results in 4-8 weeks, while peptide therapy may show benefits in 2-6 weeks. We monitor your progress closely and adjust treatments as needed.'
  },
  {
    question: 'What is included in the lab testing?',
    answer: 'Our comprehensive lab panels test for hormones, vitamins, minerals, inflammatory markers, metabolic function, and other key biomarkers relevant to your treatment goals. The at-home testing kit includes everything needed for sample collection, and results are typically available within 3-5 business days.'
  },
  {
    question: 'Is telemedicine covered by insurance?',
    answer: 'Many insurance plans now cover telemedicine consultations. We accept most major insurance plans for consultations. However, specialized treatments like peptide therapy and bioidentical hormones may not be covered. We provide detailed receipts that you can submit to your insurance for potential reimbursement.'
  },
  {
    question: 'How often will I need follow-up appointments?',
    answer: 'Follow-up frequency depends on your treatment plan. Initially, we typically schedule check-ins every 2-4 weeks to monitor progress and adjust treatments. Once your treatment is optimized, follow-ups may be every 3-6 months. Our medical team is always available for questions or concerns between appointments.'
  },
  {
    question: 'What if I experience side effects?',
    answer: 'While side effects are rare with our personalized approach, our medical team is available 24/7 to address any concerns. We start with conservative dosing and gradually adjust based on your response. If you experience any side effects, we can quickly modify your treatment plan to ensure your safety and comfort.'
  },
  {
    question: 'Can I combine multiple treatments?',
    answer: 'Yes, many of our patients benefit from combination therapy. Our medical team will assess your individual needs and health profile to determine which treatments can be safely combined for optimal results. We carefully monitor all interactions and adjust protocols as needed.'
  }
];

const FAQSection: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked <span className="text-blue-600">Questions</span>
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to common questions about our <strong>telemedicine treatments</strong> and process.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                <i className={`ri-arrow-down-s-line text-2xl text-gray-500 transition-transform duration-300 ${
                  openFAQ === index ? 'rotate-180' : ''
                }`}></i>
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${
                openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-blue-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h3>
            <p className="text-gray-600 mb-6">
              Our medical team is here to help. Schedule a free consultation to discuss your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-line mr-2"></i>
                Schedule Free Consultation
              </button>
              <button 
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-chat-3-line mr-2"></i>
                Chat with Us Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;